import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { AlertCircle, ArrowLeft, CalendarClock, Check, Copy, ExternalLink, FileCode2, Loader2, RefreshCw, RotateCcw, Search, Shuffle, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type WorkbenchTopic = {
  id: number;
  title: string;
  mainKeyword: string;
  targetPage?: string | null;
  emphasis?: string | null;
  nextAction?: string | null;
  sourceType: string;
  status: string;
  publicationUrl?: string | null;
  publishedAt?: string | Date | null;
  citationSummary?: {
    statusLabel: string;
    lastCheckedAt?: string | Date | null;
    platforms?: Array<{
      provider: string;
      sourceType: string;
      count: number;
      latestAt?: string | Date | null;
    }>;
    latestExcerpt?: string | null;
    consecutiveCitedRuns?: number;
    consecutiveMissedRuns?: number;
  };
  suggestedAt: string | Date;
};

type WorkbenchOutput = {
  id: number;
  topicId: number;
  version: number;
  title: string;
  htmlContent: string;
  schemaJson: string;
  createdAt: string | Date;
};

function formatSchemaScript(schemaJson: string) {
  const trimmed = schemaJson.trim();
  if (/^<script\b/i.test(trimmed)) return trimmed;
  return `<script type="application/ld+json">\n${trimmed}\n</script>`;
}

type WorkbenchGenerationLog = {
  id: number;
  topicId: number;
  outputVersion: number;
  stepKey: string;
  stepTitle: string;
  status: "active" | "done" | "warn" | "error";
  message: string;
  createdAt: string | Date;
};

type WorkbenchGenerationDraft = {
  id: number;
  topicId: number;
  outputVersion: number;
  status: "drafting" | "blocked" | "complete";
  currentStep: string;
  errorMessage?: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
};

const ACTIVE_STATUSES = ["suggested", "brief_confirmed", "drafting", "ready_to_publish"];
const TRACKING_STATUSES = ["tracking", "cited", "citation_lost", "update_suggested"];

const STATUS_LABELS: Record<string, string> = {
  suggested: "待確認",
  brief_confirmed: "待確認",
  drafting: "準備中",
  ready_to_publish: "待複製",
  tracking: "追蹤中",
  cited: "已引用",
  citation_lost: "引用流失",
  update_suggested: "建議更新",
  archived: "已封存",
};

const STALLED_GENERATION_MS = 5 * 60 * 1000;

function generationStepLabel(step: string) {
  const section = step.match(/^section_(\d+)$/);
  if (section) return `第 ${section[1]} 章`;
  return ({
    external_knowledge: "外部知識查證",
    planning: "文章大綱",
    intro: "文章前言",
    table: "判斷清單",
    title: "文章標題",
    enrichment: "FAQ 與 Schema",
    review: "產後驗收",
    complete: "完成",
  } as Record<string, string>)[step] || "未完成階段";
}

export function isArticleGenerationVersionMatch(
  expectedOutputVersion?: number | null,
  actualOutputVersion?: number | null
) {
  if (!expectedOutputVersion) return true;
  return Number(actualOutputVersion || 0) === expectedOutputVersion;
}

export function resolveArticleGenerationVersion(
  expectedOutputVersion: number | null,
  draft?: Pick<WorkbenchGenerationDraft, "outputVersion"> | null
) {
  const draftOutputVersion = Number(draft?.outputVersion || 0);
  return draftOutputVersion || expectedOutputVersion;
}

export function isArticleGenerationWaiting(input: {
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
  hasOutput: boolean;
}) {
  if (input.hasOutput) return false;
  if (input.generationStarted) {
    if (!isArticleGenerationVersionMatch(input.expectedOutputVersion, input.draftOutputVersion)) {
      return true;
    }
    return input.draftStatus !== "blocked" && input.draftStatus !== "complete";
  }
  if (input.draftStatus === "blocked" || input.draftStatus === "complete") return false;
  return input.draftStatus === "drafting";
}

export function shouldPollArticleGenerationDraft(input: {
  requestPending: boolean;
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  topicStatus?: string | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
}) {
  const isMatchingTerminalDraft =
    (input.draftStatus === "blocked" || input.draftStatus === "complete") &&
    isArticleGenerationVersionMatch(input.expectedOutputVersion, input.draftOutputVersion);
  if (input.requestPending) return true;
  if (isMatchingTerminalDraft) return false;
  if (input.generationStarted) {
    if (!isArticleGenerationVersionMatch(input.expectedOutputVersion, input.draftOutputVersion)) {
      return true;
    }
    return input.draftStatus !== "blocked" && input.draftStatus !== "complete";
  }
  if (input.draftStatus === "blocked" || input.draftStatus === "complete") return false;
  return input.topicStatus === "drafting" || input.draftStatus === "drafting";
}

export function shouldPollArticleGenerationOutput(input: {
  generationStarted: boolean;
  expectedOutputVersion?: number | null;
  draftStatus?: WorkbenchGenerationDraft["status"] | null;
  draftOutputVersion?: number | null;
  hasOutput: boolean;
}) {
  if (!input.generationStarted || input.hasOutput) return false;
  const isCurrentTerminalDraft =
    (input.draftStatus === "blocked" || input.draftStatus === "complete") &&
    isArticleGenerationVersionMatch(input.expectedOutputVersion, input.draftOutputVersion);
  return !isCurrentTerminalDraft;
}

export function getArticleGenerationCurrentStep(
  expectedOutputVersion: number | null,
  draftOutputVersion?: number | null,
  currentStep?: string | null
) {
  if (!isArticleGenerationVersionMatch(expectedOutputVersion, draftOutputVersion)) return null;
  return currentStep || null;
}

export function selectArticleGenerationLogs(
  logs: WorkbenchGenerationLog[],
  expectedOutputVersion: number | null,
  generationStartedAt: number | null,
  requestPending: boolean
) {
  if (expectedOutputVersion) {
    return logs.filter((log) => Number(log.outputVersion || 0) === expectedOutputVersion);
  }
  if (!generationStartedAt || !requestPending) return logs;
  return logs.filter((log) => new Date(log.createdAt).getTime() >= generationStartedAt - 3000);
}

export function getArticleGenerationOutputCreatedAfter(
  generationStartedAt: number | null,
  expectedOutputVersion: number | null
) {
  if (!generationStartedAt || expectedOutputVersion) return undefined;
  return new Date(generationStartedAt - 3000).toISOString();
}

export function getNextArticleGenerationVersion(...knownVersions: Array<number | null | undefined>) {
  return Math.max(0, ...knownVersions.map((version) => Number(version || 0))) + 1;
}

export function getEmptyGenerationProgressMessage(currentStep?: string | null, blocked = false) {
  if (!currentStep) return "正在建立產文任務";
  const step = generationStepLabel(currentStep);
  return blocked ? `已保存進度：停在${step}` : `正在處理：${step}`;
}

export function shouldShowArticleGenerationProgress(input: {
  waiting: boolean;
  hasOutput: boolean;
  draftBlocked: boolean;
}) {
  if (input.hasOutput) return false;
  return input.waiting || input.draftBlocked;
}

type GenerationIssue = {
  title: string;
  message: string;
  actionLabel: string;
};

function isAbortLikeError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || "");
  return /aborted|AbortError|signal is aborted/i.test(message);
}

export function isOutputFreshForCurrentGeneration(
  output: Pick<WorkbenchOutput, "version" | "createdAt">,
  generationStartedAt: number | null,
  expectedOutputVersion: number | null
) {
  if (expectedOutputVersion) {
    return Number(output.version || 0) === expectedOutputVersion;
  }
  if (!generationStartedAt) return true;
  const createdAt = new Date(output.createdAt).getTime();
  if (Number.isNaN(createdAt)) return false;
  return createdAt >= generationStartedAt - 3000;
}

function formatDate(value?: string | Date | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getSearchConsoleStatus(topic: WorkbenchTopic) {
  if (!topic.publicationUrl) {
    return {
      label: "尚未發布",
      description: "貼上官網文章網址後才能檢查收錄。",
      tone: "muted",
    };
  }

  return {
    label: "尚未檢查",
    description: "尚未取得 Search Console 檢查結果。",
    tone: "muted",
  };
}

function getCitationStatus(topic: WorkbenchTopic) {
  const summary = topic.citationSummary;
  if (!topic.publicationUrl) {
    return {
      label: "尚未發布",
      description: "貼上官網文章網址後才會列入引用追蹤。",
      tone: "muted",
    };
  }

  if (!summary?.lastCheckedAt) {
    return {
      label: "尚未檢查",
      description: "文章已列入追蹤，還沒有引用檢查紀錄。",
      tone: "muted",
    };
  }

  if (summary.statusLabel === "已引用") {
    const platforms = summary.platforms?.map((item) => item.provider).filter(Boolean);
    return {
      label: "已引用",
      description: platforms?.length
        ? `最近引用來源：${Array.from(new Set(platforms)).join("、")}`
        : "最近檢查有引用到這篇文章。",
      tone: "success",
    };
  }

  return {
    label: "尚未引用",
    description: `最後檢查：${formatDate(summary.lastCheckedAt)}`,
    tone: "muted",
  };
}

function statusBadgeClass(tone: string) {
  if (tone === "success") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (tone === "warning") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-border bg-muted/50 text-muted-foreground";
}

function inferAudience(topic: WorkbenchTopic) {
  const text = `${topic.title} ${topic.mainKeyword} ${topic.emphasis || ""}`;
  if (/企業|公司|採購|客戶|廠商|員工|福委/.test(text)) return "企業採購、行政總務、業務送禮窗口";
  if (/長輩|爸媽|父母|年節|過年/.test(text)) return "年節送禮給長輩的人";
  if (/親友|朋友|同事/.test(text)) return "送親友、同事或一般拜訪伴手禮的人";
  if (/台中|豐原|伴手禮/.test(text)) return "正在找台中伴手禮的人";
  return "正在挑選正式甜點禮盒的人";
}

function inferScenario(topic: WorkbenchTopic) {
  const text = `${topic.title} ${topic.mainKeyword} ${topic.emphasis || ""}`;
  if (/企業|公司|採購|客戶|廠商|員工|福委/.test(text)) return "正式商務送禮、節慶大量採購";
  if (/中秋|月餅/.test(text)) return "中秋送禮，不想只送月餅";
  if (/過年|年節|春節/.test(text)) return "過年拜訪、年節送禮";
  if (/台中|豐原|伴手禮/.test(text)) return "拜訪客戶、返鄉或旅遊伴手禮";
  return "需要體面、好分食、好保存的送禮情境";
}

function inferProductRole(topic: WorkbenchTopic) {
  const text = `${topic.title} ${topic.mainKeyword}`;
  if (/曲奇|餅乾/.test(text)) return "曲奇餅乾作為安全口味與體面包裝主角，搭配節慶禮盒承接。";
  if (/堅果|塔/.test(text)) return "堅果塔作為正式送禮主角，補上口感、保存與分食判斷。";
  if (/檸檬|鳳凰|酥/.test(text)) return "熟成檸檬酥作為不想送月餅時的替代選擇，搭配節慶禮盒。";
  return "堅果塔、曲奇餅乾、熟成檸檬酥依收禮對象與節慶情境分工。";
}

function buildConfirmationRows(topic: WorkbenchTopic) {
  return [
    ["要寫給誰", inferAudience(topic)],
    ["使用情境", inferScenario(topic)],
    ["主要問題", topic.title],
    ["商品角色", inferProductRole(topic)],
    ["承接頁面", topic.targetPage || "https://www.idodo.com.tw/"],
    ["FAQ 方向", "送禮對象怎麼分、商品怎麼選、保存與分食怎麼判斷。"],
    ["不要寫什麼", "不寫泛排行、不亂報價格、不杜撰得獎或庫存。"],
  ] as const;
}

function collapseGenerationLogs(logs: WorkbenchGenerationLog[]): WorkbenchGenerationLog[] {
  const rowsByStep = new Map<string, WorkbenchGenerationLog>();
  const stepOrder: string[] = [];

  for (const log of logs) {
    if (!rowsByStep.has(log.stepKey)) {
      stepOrder.push(log.stepKey);
    }
    rowsByStep.set(log.stepKey, log);
  }

  return stepOrder
    .map((stepKey) => rowsByStep.get(stepKey))
    .filter((log): log is WorkbenchGenerationLog => Boolean(log));
}

function getLatestAttemptVersion(logs: WorkbenchGenerationLog[]) {
  return logs.reduce((version, log) => Math.max(version, Number(log.outputVersion || 0)), 0);
}

function isOutputBehindLatestAttempt(output: WorkbenchOutput, logs: WorkbenchGenerationLog[]) {
  const latestAttemptVersion = getLatestAttemptVersion(logs);
  return latestAttemptVersion > Number(output.version || 0);
}

function getGenerationIssue(params: {
  logs: WorkbenchGenerationLog[];
  currentOutput: WorkbenchOutput | null;
  now: number;
}): GenerationIssue | null {
  if (params.currentOutput && !isOutputBehindLatestAttempt(params.currentOutput, params.logs)) {
    return null;
  }

  const visibleLogs = collapseGenerationLogs(params.logs);
  const latestLog = visibleLogs[visibleLogs.length - 1];
  const latestError = [...visibleLogs].reverse().find((log) => log.status === "error");
  if (latestError) {
    return {
      title: "產文失敗",
      message: latestError.message || "文章沒有產出可複製內容。",
      actionLabel: "重新產文",
    };
  }

  if (latestLog?.status === "active") {
    const lastUpdatedAt = new Date(latestLog.createdAt).getTime();
    if (!Number.isNaN(lastUpdatedAt) && params.now - lastUpdatedAt > STALLED_GENERATION_MS) {
      return {
        title: "產文中斷",
        message: `停在「${latestLog.stepTitle}」超過 5 分鐘，沒有產出可複製內容。`,
        actionLabel: "重新產文",
      };
    }
  }

  return null;
}

function GenerationProgressPanel({
  logs,
  issue,
  currentStep,
  onRegenerate,
  regenerateDisabled,
}: {
  logs: WorkbenchGenerationLog[];
  issue?: GenerationIssue | null;
  currentStep?: string | null;
  onRegenerate?: () => void;
  regenerateDisabled?: boolean;
}) {
  const visibleLogs = collapseGenerationLogs(logs).filter((log) => log.status !== "error");
  const latestActive = [...visibleLogs].reverse().find((log) => log.status === "active");
  const latestLog = visibleLogs[visibleLogs.length - 1];
  const headline = latestActive || latestLog;

  return (
    <div className="space-y-5">
      {issue ? (
        <div className="flex items-start justify-between gap-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <AlertCircle className="mt-0.5 h-6 w-6 text-destructive" />
            <div className="min-w-0">
              <p className="text-base font-semibold text-destructive">{issue.title}</p>
              <p className="text-sm leading-6 text-muted-foreground">{issue.message}</p>
            </div>
          </div>
          {onRegenerate ? (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 gap-2"
              onClick={onRegenerate}
              disabled={regenerateDisabled}
            >
              {regenerateDisabled ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {issue.actionLabel}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex items-start gap-3">
          {headline?.status === "error" ? (
            <AlertCircle className="mt-0.5 h-6 w-6 text-destructive" />
          ) : headline?.status === "done" ? (
            <Check className="mt-0.5 h-6 w-6 text-emerald-600" />
          ) : (
            <Loader2 className="mt-0.5 h-6 w-6 animate-spin text-foreground" />
          )}
          <div className="min-w-0">
            <p className="text-base font-semibold text-foreground">
              {headline?.stepTitle || "正在建立產文任務"}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              {headline?.message || "產文開始後，這裡會即時顯示目前做到哪一步。"}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {visibleLogs.length === 0 ? (
          <div className="rounded-md border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
            {getEmptyGenerationProgressMessage(currentStep, Boolean(issue))}
          </div>
        ) : (
          visibleLogs.map((log) => (
            <div key={log.stepKey} className="grid grid-cols-[24px_minmax(0,1fr)] gap-3 rounded-md border px-3 py-3">
              <div className="pt-0.5">
                {log.status === "active" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-foreground" />
                ) : log.status === "done" ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle className={log.status === "error" ? "h-4 w-4 text-destructive" : "h-4 w-4 text-amber-600"} />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-foreground">{log.stepTitle}</p>
                  <span className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm leading-6 text-muted-foreground">{log.message}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ArticleWorkbench() {
  const { user, loading } = useAuth();
  const autoEnsureRef = useRef(false);
  const generationStartedAtRef = useRef<number | null>(null);
  const generationBaselineVersionRef = useRef(0);
  const completedDraftRefetchVersionRef = useRef<number | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [currentOutput, setCurrentOutput] = useState<WorkbenchOutput | null>(null);
  const [publicationUrl, setPublicationUrl] = useState("");
  const [copiedField, setCopiedField] = useState<"html" | "schema" | null>(null);
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(null);
  const [generationExpectedVersion, setGenerationExpectedVersion] = useState<number | null>(null);
  const [generationNow, setGenerationNow] = useState(Date.now());
  const [activeTab, setActiveTab] = useState<"writing" | "tracking">("writing");

  const markGenerationStarted = (expectedOutputVersion: number | null = null) => {
    const now = Date.now();
    generationStartedAtRef.current = now;
    setGenerationStartedAt(now);
    setGenerationExpectedVersion(expectedOutputVersion);
    return now;
  };

  const stopGenerationWaiting = () => {
    generationStartedAtRef.current = null;
    setGenerationStartedAt(null);
  };

  const clearGenerationStarted = () => {
    stopGenerationWaiting();
    setGenerationExpectedVersion(null);
  };

  const topicsQuery = trpc.articleWorkbench.list.useQuery(undefined, {
    enabled: Boolean(user) && !loading,
    refetchOnWindowFocus: false,
  });

  const topics = useMemo(
    () =>
      ((topicsQuery.data || []) as WorkbenchTopic[])
        .filter((topic) => ACTIVE_STATUSES.includes(topic.status))
        .slice(0, 5),
    [topicsQuery.data]
  );

  const trackingTopics = useMemo(
    () =>
      ((topicsQuery.data || []) as WorkbenchTopic[])
        .filter((topic) => TRACKING_STATUSES.includes(topic.status) && Boolean(topic.publicationUrl))
        .sort((left, right) => new Date(right.publishedAt || right.suggestedAt).getTime() - new Date(left.publishedAt || left.suggestedAt).getTime()),
    [topicsQuery.data]
  );

  const selectedTopic = useMemo(
    () => ((topicsQuery.data || []) as WorkbenchTopic[]).find((topic) => topic.id === selectedTopicId) || null,
    [selectedTopicId, topicsQuery.data]
  );

  const ensureQueue = trpc.articleWorkbench.ensureQueue.useMutation({
    onSuccess: () => topicsQuery.refetch(),
    onError: (error) => toast.error(error.message),
  });

  const regenerateQueue = trpc.articleWorkbench.regenerateQueue.useMutation({
    onSuccess: (result) => {
      if (result.created > 0) {
        toast.success("已重新生成五題");
      } else {
        toast.warning(result.reason || "目前沒有可用資料可重新生成");
      }
      topicsQuery.refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const prepareOutput = trpc.articleWorkbench.prepareOutput.useMutation({
    onMutate: () => {
      if (!generationStartedAtRef.current) {
        markGenerationStarted();
      }
    },
    onSuccess: (output) => {
      setCurrentOutput(output as WorkbenchOutput);
      clearGenerationStarted();
      generationDraftQuery.refetch();
      generationLogsQuery.refetch();
      topicsQuery.refetch();
    },
    onError: (error) => {
      if (isAbortLikeError(error)) {
        toast.warning("產文仍在進行，完成後會自動切到可複製畫面");
        generationDraftQuery.refetch();
        generationLogsQuery.refetch();
        currentOutputQuery.refetch();
        topicsQuery.refetch();
        return;
      }
      stopGenerationWaiting();
      generationDraftQuery.refetch();
      generationLogsQuery.refetch();
      toast.error(error.message || "產文流程中斷");
    },
  });

  const generationDraftQuery = trpc.articleWorkbench.generationDraft.useQuery(
    { id: selectedTopicId || 1 },
    {
      enabled: Boolean(user) && Boolean(selectedTopicId),
      refetchInterval: (query) => {
        const draft = query.state.data as WorkbenchGenerationDraft | null | undefined;
        const pollingVersion = resolveArticleGenerationVersion(generationExpectedVersion, draft);
        return shouldPollArticleGenerationDraft({
          requestPending: prepareOutput.isPending,
          generationStarted: Boolean(generationStartedAt),
          expectedOutputVersion: pollingVersion,
          topicStatus: selectedTopic?.status,
          draftStatus: draft?.status,
          draftOutputVersion: draft?.outputVersion,
        }) ? 1200 : false;
      },
      refetchOnWindowFocus: false,
    }
  );
  const generationDraft = generationDraftQuery.data as WorkbenchGenerationDraft | null | undefined;
  const activeGenerationVersion = prepareOutput.isPending
    ? generationExpectedVersion
    : resolveArticleGenerationVersion(generationExpectedVersion, generationDraft);
  const isWaitingForOutput = isArticleGenerationWaiting({
    generationStarted: Boolean(generationStartedAt),
    expectedOutputVersion: activeGenerationVersion,
    draftStatus: generationDraft?.status,
    draftOutputVersion: generationDraft?.outputVersion,
    hasOutput: Boolean(currentOutput),
  });
  const shouldPollCurrentOutput = shouldPollArticleGenerationOutput({
    generationStarted: Boolean(generationStartedAt),
    expectedOutputVersion: activeGenerationVersion,
    draftStatus: generationDraft?.status,
    draftOutputVersion: generationDraft?.outputVersion,
    hasOutput: Boolean(currentOutput),
  });

  const currentOutputQuery = trpc.articleWorkbench.currentOutput.useQuery(
    {
      id: selectedTopicId || 1,
      createdAfter: getArticleGenerationOutputCreatedAfter(generationStartedAt, activeGenerationVersion),
    },
    {
      enabled: Boolean(user) && Boolean(selectedTopicId) && !currentOutput,
      refetchInterval: isWaitingForOutput || shouldPollCurrentOutput ? 1500 : false,
      refetchOnWindowFocus: false,
    }
  );

  const generationLogsQuery = trpc.articleWorkbench.generationLogs.useQuery(
    { id: selectedTopicId || 1 },
    {
      enabled: Boolean(user) && Boolean(selectedTopicId),
      refetchInterval: isWaitingForOutput || selectedTopic?.status === "drafting" ? 1200 : false,
      refetchOnWindowFocus: false,
    }
  );
  const rawGenerationLogs = useMemo(
    () => ((generationLogsQuery.data || []) as WorkbenchGenerationLog[]),
    [generationLogsQuery.data]
  );

  const archiveTopic = trpc.articleWorkbench.archive.useMutation({
    onSuccess: () => {
      toast.success("已換一題");
      setSelectedTopicId(null);
      setCurrentOutput(null);
      setPublicationUrl("");
      topicsQuery.refetch();
      ensureQueue.mutate({ targetCount: 5 });
    },
    onError: (error) => toast.error(error.message),
  });

  const publishTopic = trpc.articleWorkbench.publish.useMutation({
    onSuccess: () => {
      toast.success("已開始追蹤");
      setSelectedTopicId(null);
      setCurrentOutput(null);
      setPublicationUrl("");
      setActiveTab("tracking");
      topicsQuery.refetch();
      ensureQueue.mutate({ targetCount: 5 });
    },
    onError: (error) => toast.error(error.message),
  });

  useEffect(() => {
    if (!user || loading) return;
    if (autoEnsureRef.current) return;
    autoEnsureRef.current = true;
    ensureQueue.mutate({ targetCount: 5 });
  }, [user, loading]);

  useEffect(() => {
    if (!selectedTopic) return;
    setCurrentOutput(null);
    setPublicationUrl("");
    generationBaselineVersionRef.current = 0;
    completedDraftRefetchVersionRef.current = null;
    clearGenerationStarted();
  }, [selectedTopic?.id]);

  useEffect(() => {
    if (!isWaitingForOutput && selectedTopic?.status !== "drafting") return;
    const timer = window.setInterval(() => setGenerationNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isWaitingForOutput, selectedTopic?.status]);

  useEffect(() => {
    if (!generationStartedAt) return;
    if (generationDraft?.status !== "blocked") return;
    if (!isArticleGenerationVersionMatch(activeGenerationVersion, generationDraft.outputVersion)) return;
    stopGenerationWaiting();
  }, [generationDraft?.status, generationDraft?.outputVersion, activeGenerationVersion, generationStartedAt]);

  useEffect(() => {
    if (currentOutput || generationDraft?.status !== "complete") return;
    if (!isArticleGenerationVersionMatch(activeGenerationVersion, generationDraft.outputVersion)) return;
    const completedVersion = Number(generationDraft.outputVersion || 0);
    if (!completedVersion || completedDraftRefetchVersionRef.current === completedVersion) return;
    completedDraftRefetchVersionRef.current = completedVersion;
    currentOutputQuery.refetch();
  }, [
    currentOutput,
    generationDraft?.status,
    generationDraft?.outputVersion,
    activeGenerationVersion,
    currentOutputQuery.refetch,
  ]);

  useEffect(() => {
    if (!currentOutputQuery.data || currentOutput) return;
    const output = currentOutputQuery.data as WorkbenchOutput;
    if (isOutputBehindLatestAttempt(output, rawGenerationLogs)) return;
    if (!isOutputFreshForCurrentGeneration(output, generationStartedAtRef.current, activeGenerationVersion)) return;
    setCurrentOutput(output);
    clearGenerationStarted();
  }, [currentOutputQuery.data, currentOutput, activeGenerationVersion, generationStartedAt, rawGenerationLogs]);

  const handleCopy = async (type: "html" | "schema") => {
    if (!currentOutput) return;
    const textToCopy = type === "html" ? currentOutput.htmlContent : formatSchemaScript(currentOutput.schemaJson);
    await navigator.clipboard.writeText(textToCopy);
    setCopiedField(type);
    setTimeout(() => setCopiedField(null), 1800);
    toast.success(type === "html" ? "HTML 已複製" : "Schema 已複製");
  };

  const handlePublish = () => {
    if (!selectedTopic || !publicationUrl.trim()) return;
    publishTopic.mutate({ id: selectedTopic.id, publicationUrl: publicationUrl.trim() });
  };

  const handleRegenerateOutput = () => {
    if (!selectedTopic) return;
    const expectedOutputVersion = getNextArticleGenerationVersion(
      generationBaselineVersionRef.current,
      Number(currentOutput?.version || 0),
      Number(currentOutputQuery.data?.version || 0),
      Number(generationDraft?.outputVersion || 0),
      getLatestAttemptVersion(rawGenerationLogs)
    );
    generationBaselineVersionRef.current = expectedOutputVersion - 1;
    markGenerationStarted(expectedOutputVersion);
    setCurrentOutput(null);
    setPublicationUrl("");
    setCopiedField(null);
    prepareOutput.mutate({ id: selectedTopic.id, forceNew: true });
  };

  const handleStartGenerate = () => {
    if (!selectedTopic) return;
    const expectedOutputVersion = getNextArticleGenerationVersion(
      generationBaselineVersionRef.current,
      Number(generationDraft?.outputVersion || 0),
      Number(currentOutputQuery.data?.version || 0),
      getLatestAttemptVersion(rawGenerationLogs)
    );
    markGenerationStarted(expectedOutputVersion);
    setCurrentOutput(null);
    prepareOutput.mutate({ id: selectedTopic.id, forceNew: true });
  };

  const handleContinueGenerate = () => {
    if (!selectedTopic) return;
    const resumeVersion = Number(generationDraft?.outputVersion || getLatestAttemptVersion(rawGenerationLogs) || 0);
    markGenerationStarted(resumeVersion || null);
    setCurrentOutput(null);
    prepareOutput.mutate({ id: selectedTopic.id, forceNew: false });
  };

  const handleChangeTopic = () => {
    if (!selectedTopic) return;
    archiveTopic.mutate({ id: selectedTopic.id });
  };

  if (selectedTopic) {
    const isPublishedTopic = TRACKING_STATUSES.includes(selectedTopic.status);
    const isPreparing = isWaitingForOutput;
    const isRegenerating = prepareOutput.isPending && Boolean(currentOutput);
    const confirmationRows = buildConfirmationRows(selectedTopic);
    const generationLogs = selectArticleGenerationLogs(
      rawGenerationLogs,
      activeGenerationVersion,
      generationStartedAt,
      prepareOutput.isPending
    );
    const isCurrentDraftBlocked =
      generationDraft?.status === "blocked" &&
      isArticleGenerationVersionMatch(activeGenerationVersion, generationDraft.outputVersion);
    const generationIssue = !currentOutput && isCurrentDraftBlocked
      ? {
        title: `產文停在「${generationStepLabel(generationDraft.currentStep)}」`,
        message: generationDraft.errorMessage || "這個階段尚未通過，已完成的內容已保留。",
        actionLabel: "繼續產文",
      }
      : getGenerationIssue({
        logs: generationLogs,
        currentOutput,
        now: generationNow,
      });
    const shouldShowGenerationPanel = shouldShowArticleGenerationProgress({
      waiting: isPreparing,
      hasOutput: Boolean(currentOutput),
      draftBlocked: Boolean(isCurrentDraftBlocked),
    });
    const displayOutput = currentOutput;
    const searchConsoleStatus = getSearchConsoleStatus(selectedTopic);
    const citationStatus = getCitationStatus(selectedTopic);

    return (
      <DashboardLayout>
        <main className="min-h-screen bg-background">
          <section className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-5 py-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 border-b pb-5">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                onClick={() => {
                  setSelectedTopicId(null);
                  setCurrentOutput(null);
                  setPublicationUrl("");
                }}
              >
                <ArrowLeft className="h-4 w-4" />
                返回
              </Button>
              <Badge variant="outline" className="gap-1">
                <CalendarClock className="h-3.5 w-3.5" />
                {formatDate(selectedTopic.suggestedAt)}
              </Badge>
            </div>

            <div className="grid gap-4 py-5">
              <Card>
                <CardContent className="space-y-3 p-5">
                  <Badge variant="secondary">{STATUS_LABELS[selectedTopic.status] || "待複製"}</Badge>
                  <h1 className="text-xl font-semibold tracking-normal">{selectedTopic.title}</h1>
                  {isPublishedTopic && selectedTopic.publicationUrl ? (
                    <a
                      href={selectedTopic.publicationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 items-center gap-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      <ExternalLink className="h-4 w-4 shrink-0" />
                      <span className="truncate">{selectedTopic.publicationUrl}</span>
                    </a>
                  ) : null}
                </CardContent>
              </Card>

              {isPublishedTopic ? (
                <Card>
                  <CardContent className="grid gap-3 p-5 sm:grid-cols-2">
                    <div className="rounded-md border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">Search Console 收錄</p>
                        <Badge variant="outline" className={statusBadgeClass(searchConsoleStatus.tone)}>
                          {searchConsoleStatus.label}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{searchConsoleStatus.description}</p>
                    </div>
                    <div className="rounded-md border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium">AIO / LLM 引用</p>
                        <Badge variant="outline" className={statusBadgeClass(citationStatus.tone)}>
                          {citationStatus.label}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{citationStatus.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {isPublishedTopic && !currentOutput && !shouldShowGenerationPanel ? (
                <Card>
                  <CardContent className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
                    {currentOutputQuery.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCode2 className="h-4 w-4" />}
                    {currentOutputQuery.isLoading ? "載入原本產出的 HTML 與 Schema" : "這篇文章沒有找到可複製內容"}
                  </CardContent>
                </Card>
              ) : null}

              {!isPublishedTopic && !currentOutput && !shouldShowGenerationPanel ? (
                <Card>
                  <CardContent className="space-y-5 p-5">
                    <div className="overflow-hidden rounded-md border">
                      <table className="w-full text-sm">
                        <tbody>
                          {confirmationRows.map(([label, value]) => (
                            <tr key={label} className="border-b last:border-b-0">
                              <th className="w-32 bg-muted/60 px-3 py-3 text-left align-top font-medium text-foreground">
                                {label}
                              </th>
                              <td className="px-3 py-3 leading-6 text-muted-foreground">{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                      <Button
                        size="lg"
                        className="h-12 gap-2"
                        onClick={handleStartGenerate}
                        disabled={prepareOutput.isPending || archiveTopic.isPending}
                      >
                        <Sparkles className="h-4 w-4" />
                        開始產生文章
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="lg"
                            variant="outline"
                            className="h-12 gap-2"
                            disabled={prepareOutput.isPending || archiveTopic.isPending}
                          >
                            <Shuffle className="h-4 w-4" />
                            換一題
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>換掉這題？</AlertDialogTitle>
                            <AlertDialogDescription>
                              這題會從五題清單移除，系統會補一題新的。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>取消</AlertDialogCancel>
                            <AlertDialogAction onClick={handleChangeTopic}>換一題</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              {shouldShowGenerationPanel || displayOutput ? (
              <Card>
                <CardContent className="space-y-4 p-5">
                  {shouldShowGenerationPanel ? (
                    <div className="min-h-64">
                      <GenerationProgressPanel
                        logs={generationLogs}
                        issue={generationIssue}
                        currentStep={getArticleGenerationCurrentStep(
                          activeGenerationVersion,
                          generationDraft?.outputVersion,
                          generationDraft?.currentStep
                        )}
                        onRegenerate={isCurrentDraftBlocked ? handleContinueGenerate : handleRegenerateOutput}
                        regenerateDisabled={prepareOutput.isPending}
                      />
                    </div>
                  ) : null}

                  {displayOutput ? (
                    <>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Button
                          size="lg"
                          className="h-14 gap-2"
                          onClick={() => handleCopy("html")}
                          disabled={isRegenerating}
                        >
                          {copiedField === "html" ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                          複製 HTML
                        </Button>
                        <Button
                          size="lg"
                          variant="outline"
                          className="h-14 gap-2"
                          onClick={() => handleCopy("schema")}
                          disabled={isRegenerating}
                        >
                          {copiedField === "schema" ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                          複製 Schema
                        </Button>
                      </div>

                      <details className="rounded-md border bg-muted/30 p-3 text-sm">
                        <summary className="cursor-pointer font-medium">檢查內容</summary>
                        <div className="mt-3 grid gap-3">
                          <pre className="max-h-48 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                            {displayOutput.htmlContent}
                          </pre>
                          <pre className="max-h-48 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                            {displayOutput.schemaJson}
                          </pre>
                        </div>
                      </details>
                    </>
                  ) : null}
                </CardContent>
              </Card>
              ) : null}

              {displayOutput && !isPublishedTopic ? (
              <Card>
                <CardContent className="space-y-3 p-5">
                  <Input
                    value={publicationUrl}
                    onChange={(event) => setPublicationUrl(event.target.value)}
                    placeholder="貼上鴻鼎官網文章網址"
                    inputMode="url"
                  />
                  <Button
                    className="h-12 w-full gap-2"
                    onClick={handlePublish}
                    disabled={!displayOutput || !publicationUrl.trim() || publishTopic.isPending}
                  >
                    {publishTopic.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    開始追蹤
                  </Button>
                </CardContent>
              </Card>
              ) : null}

              {displayOutput && !isPublishedTopic ? (
              <div className="flex justify-end">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" className="gap-2" disabled={prepareOutput.isPending}>
                      <RotateCcw className="h-4 w-4" />
                      重新產文
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>重新產生文章內容？</AlertDialogTitle>
                      <AlertDialogDescription>
                        系統會清除目前畫面內容，重新產生新版 HTML 與 Schema。
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>取消</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRegenerateOutput}>重新產文</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              ) : null}
            </div>
          </section>
        </main>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <main className="min-h-screen bg-background">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 py-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "writing" | "tracking")} className="flex-1 gap-0">
            <div className="flex flex-col gap-4 border-b pb-5 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-semibold tracking-normal">文章題目工作台</h1>
                <p className="text-sm text-muted-foreground">
                  五題產文與已發布文章分開管理
                </p>
              </div>
              <TabsList className="grid w-full grid-cols-2 md:w-auto">
                <TabsTrigger value="writing">現在要寫</TabsTrigger>
                <TabsTrigger value="tracking">已發布追蹤</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="writing" className="mt-0">
              <div className="flex flex-col gap-4 py-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold tracking-normal">現在要寫的五題</h2>
                  <p className="text-sm text-muted-foreground">
                    {topics.length}/5 題 · 每題都保留出題時間
                  </p>
                </div>
                <Button
                  className="w-full gap-2 md:w-auto"
                  onClick={() => regenerateQueue.mutate()}
                  disabled={regenerateQueue.isPending || ensureQueue.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  {regenerateQueue.isPending ? "重新生成中" : "重新生成"}
                </Button>
              </div>

              <div className="grid flex-1 grid-rows-[auto] gap-3 pb-5">
                {topicsQuery.isLoading ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">載入題目中</CardContent>
                  </Card>
                ) : null}

                {!topicsQuery.isLoading && topics.length === 0 ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">目前沒有可寫題目</CardContent>
                  </Card>
                ) : null}

                {topics.map((topic, index) => (
                  <Card
                    key={topic.id}
                    className="cursor-pointer border-border/80 transition-colors hover:border-foreground/30"
                    onClick={() => setSelectedTopicId(topic.id)}
                  >
                    <CardContent className="grid gap-4 p-5 md:grid-cols-[56px_minmax(0,1fr)] md:items-center">
                      <div className="flex h-11 w-11 items-center justify-center rounded-md border bg-muted text-base font-semibold">
                        {index + 1}
                      </div>
                      <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="gap-1">
                            <CalendarClock className="h-3.5 w-3.5" />
                            {formatDate(topic.suggestedAt)}
                          </Badge>
                          <Badge variant="secondary">{STATUS_LABELS[topic.status] || "待複製"}</Badge>
                        </div>
                        <h2 className="text-lg font-semibold tracking-normal">{topic.title}</h2>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="tracking" className="mt-0">
              <div className="py-5">
                <h2 className="text-xl font-semibold tracking-normal">已發布追蹤</h2>
                <p className="text-sm text-muted-foreground">
                  {trackingTopics.length} 篇 · 可查看官網文章、收錄狀態、引用狀態與原本 HTML / Schema
                </p>
              </div>

              <div className="grid gap-3 pb-5">
                {topicsQuery.isLoading ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">載入追蹤文章中</CardContent>
                  </Card>
                ) : null}

                {!topicsQuery.isLoading && trackingTopics.length === 0 ? (
                  <Card>
                    <CardContent className="p-5 text-sm text-muted-foreground">目前沒有已發布追蹤文章</CardContent>
                  </Card>
                ) : null}

                {trackingTopics.map((topic) => {
                  const searchConsoleStatus = getSearchConsoleStatus(topic);
                  const citationStatus = getCitationStatus(topic);
                  return (
                    <Card key={topic.id} className="border-border/80">
                      <CardContent className="space-y-4 p-5">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div className="min-w-0 space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="secondary">{STATUS_LABELS[topic.status] || "追蹤中"}</Badge>
                              <Badge variant="outline" className="gap-1">
                                <CalendarClock className="h-3.5 w-3.5" />
                                {formatDate(topic.publishedAt)}
                              </Badge>
                            </div>
                            <h3 className="text-lg font-semibold tracking-normal">{topic.title}</h3>
                            {topic.publicationUrl ? (
                              <a
                                href={topic.publicationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex max-w-full items-center gap-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                              >
                                <ExternalLink className="h-4 w-4 shrink-0" />
                                <span className="truncate">{topic.publicationUrl}</span>
                              </a>
                            ) : null}
                          </div>
                          <Button variant="outline" className="shrink-0 gap-2" onClick={() => setSelectedTopicId(topic.id)}>
                            <FileCode2 className="h-4 w-4" />
                            查看與複製
                          </Button>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-md border p-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium">Search Console 收錄</p>
                              <Badge variant="outline" className={statusBadgeClass(searchConsoleStatus.tone)}>
                                {searchConsoleStatus.label}
                              </Badge>
                            </div>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">{searchConsoleStatus.description}</p>
                          </div>
                          <div className="rounded-md border p-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium">AIO / LLM 引用</p>
                              <Badge variant="outline" className={statusBadgeClass(citationStatus.tone)}>
                                {citationStatus.label}
                              </Badge>
                            </div>
                            <p className="mt-2 text-sm leading-6 text-muted-foreground">{citationStatus.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </section>
      </main>
    </DashboardLayout>
  );
}
